<template>
  <div
    id="avatar"
    data-testid="studio-avatar"
    class="tw-relative tw-flex tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-full tw-bg-cover tw-bg-center tw-font-medium tw-leading-none tw-tracking-wider tw-text-white"
    :class="{
      'tw-h-8 tw-w-8 tw-text-xs': size === 'md',
      'tw-h-6 tw-w-6 tw-text-2xs': size === 'sm',
      'tw-h-10 tw-w-10 tw-text-sm': size === 'lg',
      'tw-text-md tw-h-12 tw-w-12': size === 'xl',
      'tw-h-4 tw-w-4 tw-text-[7px]': size === 'xs',
      'tw-cursor-pointer': clickable
    }"
    :style="{
      backgroundColor: cloudfrontSrc ? 'transparent' : getAvatarColor(),
      backgroundImage: cloudfrontSrc ? `url(${cloudfrontSrc})` : ''
    }"
    :title="name"
  >
    <div
      v-if="!cloudfrontSrc"
      class="tw-absolute tw-inset-0 tw-z-0 tw-bg-gradient-to-br tw-from-transparent tw-to-deep-orange-300"
    />
    <div v-if="!cloudfrontSrc" class="tw-relative">
      {{ getInitials() }}
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';

import { isCloudFrontURL } from '../studio/speakers/store/utils';
import { getEnv } from '../utils/env';

export default defineComponent({
  name: 'StudioAvatar',
  props: {
    imageSrc: {
      type: String,
      default: null
    },
    name: {
      type: String,
      required: true
    },
    size: {
      type: String as PropType<'md' | 'sm' | 'lg' | 'xs' | 'xl'>,
      default: 'md',
      validator: (prop: string) => ['md', 'sm', 'lg', 'xs', 'xl'].includes(prop)
    },
    clickable: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    cloudfrontSrc(): string {
      if (!this.imageSrc) {
        return '';
      }

      if (isCloudFrontURL(this.imageSrc)) {
        return this.imageSrc;
      }

      return `${getEnv('VUE_APP_CLOUDFRONT_URL')}/${this.imageSrc}`;
    }
  },
  methods: {
    getInitials() {
      if (typeof this.name !== 'string' || this.name.trim() === '') {
        return '';
      }
      const parts = this.name.trim().split(' ');
      if (parts.length > 1) {
        return (parts[0][0] || '').toUpperCase() + (parts[1][0] || '').toUpperCase();
      }
      return this.name[0] && this.name[0].toUpperCase();
    },
    getAvatarColor() {
      const colors = [
        '#ef4444',
        '#dc2626',
        '#b91c1c',
        '#f97316',
        '#ea580c',
        '#84cc16',
        '#65a30d',
        '#4d7c0f',
        '#3f6212',
        '#22c55e',
        '#16a34a',
        '#15803d',
        '#166534',
        '#14532d',
        '#10b981',
        '#059669',
        '#047857',
        '#065f46',
        '#064e3b',
        '#14b8a6',
        '#0d9488',
        '#0f766e',
        '#115e59',
        '#134e4a',
        '#06b6d4',
        '#0891b2',
        '#0e7490',
        '#155e75',
        '#164e63',
        '#0ea5e9',
        '#0284c7',
        '#0369a1',
        '#075985',
        '#0c4a6e',
        '#3b82f6',
        '#2563eb',
        '#1d4ed8',
        '#1e40af',
        '#1e3a8a',
        '#6366f1',
        '#4f46e5',
        '#4338ca',
        '#3730a3',
        '#312e81',
        '#8b5cf6',
        '#7c3aed',
        '#6d28d9',
        '#5b21b6',
        '#4c1d95',
        '#a855f7',
        '#9333ea',
        '#7e22ce',
        '#6b21a8',
        '#581c87',
        '#d946ef',
        '#c026d3',
        '#a21caf',
        '#86198f',
        '#701a75',
        '#ec4899',
        '#db2777',
        '#be185d',
        '#9d174d',
        '#831843',
        '#f43f5e',
        '#e11d48',
        '#be123c',
        '#9f1239',
        '#881337'
      ];

      if (!this.name) {
        return colors[0];
      }
      return colors[Math.abs(this.name.charCodeAt(0) % colors.length)];
    }
  }
});
</script>
